import React, { useState, useRef, useEffect } from 'react';
import OneOmicsLogo from './OneOmicsLogo';

// ─── Inline SVG icons ─────────────────────────────────────────────────────────
const Ic = ({ d, size = 16 }) => (
 <svg width={size} height={size} viewBox="0 0 16 16" fill="none"
 stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
 <path d={d} />
 </svg>
);

const ICONS = {
 upload: "M8 11V3M4 6l4-4 4 4M2 13v1a1 1 0 001 1h10a1 1 0 001-1v-1",
 report: "M3 1h10a1 1 0 011 1v12a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1zm2 3h6M5 7h6M5 10h4",
 moon: "M12 3a6 6 0 01-9 9 6 6 0 006-9z",
 sun: "M8 1v2M8 13v2M1 8h2M13 8h2M3.5 3.5l1.4 1.4M11.1 11.1l1.4 1.4M3.5 12.5l1.4-1.4M11.1 4.9l1.4-1.4M8 5a3 3 0 110 6 3 3 0 010-6z",
 logout: "M10 3h3a1 1 0 011 1v8a1 1 0 01-1 1h-3M7 11l4-3-4-3M2 8h9",
 chevronL: "M10 3L5 8l5 5",
 chevronR: "M6 3l5 5-5 5",
 dna: "M4 2c0 4 8 4 8 8S4 14 4 14M12 2c0 4-8 4-8 8s8 4 8 4",
 micro: "M8 2v3M8 11v3M4 5.5C4 7.4 5.8 9 8 9s4-1.6 4-3.5S10.2 2 8 2 4 3.6 4 5.5z",
 bacterial: "M6 3a3 3 0 00-3 3v4a3 3 0 006 0V6a3 3 0 00-3-3zm7 2a2 2 0 100 4 2 2 0 000-4zM3 12a2 2 0 100 4 2 2 0 000-4zm10 0a2 2 0 100 4 2 2 0 000-4z",
 mRNA: "M8 3a5 5 0 100 10A5 5 0 008 3zm-3 5h6M8 5v6",
 gene: "M3 4h10M3 8h10M3 12h6M13 10l-2 2 2 2",
 rna: "M4 4c0 2 8 2 8 4s-8 2-8 4M12 4c0 2-8 2-8 4s8 2 8 4",
 meta: "M8 2a6 6 0 100 12A6 6 0 008 2zm0 0v12M2 8h12",
 chevronD: "M3 6l5 5 5-5",
 shield: "M8 1l6 3v4c0 3.5-2.5 6-6 7-3.5-1-6-3.5-6-7V4l6-3z",
 clock: "M8 1a7 7 0 110 14A7 7 0 018 1zm0 3v4l3 2",
};

// ─── Report type definitions ──────────────────────────────────────────────────
export const REPORT_TYPES = [
 { id: 'wgs', label: 'WGS', sub: 'Whole Genome Sequencing', icon: 'dna', accent: '#00b4d8' },
 { id: 'bacterial_wgs', label: 'Bacterial WGS', sub: 'Bacterial Whole Genome Sequencing', icon: 'bacterial', accent: '#0d9488' },
 { id: '16sv1-v9', label: '16S (V1–V9)', sub: '16S rRNA Amplicon', icon: 'micro', accent: '#10b981' },
 { id: 'mRNA', label: 'mRNA', sub: 'mRNA Genome', icon: 'mRNA', accent: '#6366f1' },
 { id: 'meta', label: 'Shotgun Metagenomics', sub: 'Whole-Community Shotgun Sequencing', icon: 'meta', accent: '#f43f5e' },
 { id: 'wes', label: 'WES', sub: 'Whole Exome Sequencing', icon: 'gene', accent: '#a855f7' },
];

// ─── User dropdown component ──────────────────────────────────────────────────
function UserChip({ t, dark, accent, user, onLogout, onOpenAdmin }) {
 const [open, setOpen] = useState(false);
 const [sessionTime, setSessionTime] = useState('');
 const ref = useRef(null);
 const sessionStart = useRef(Date.now());

 // Update session clock every minute
 useEffect(() => {
 const fmt = () => {
 const mins = Math.floor((Date.now() - sessionStart.current) / 60000);
 if (mins < 1) return 'Just now';
 if (mins < 60) return `${mins}m session`;
 return `${Math.floor(mins / 60)}h ${mins % 60}m session`;
 };
 setSessionTime(fmt());
 const id = setInterval(() => setSessionTime(fmt()), 60000);
 return () => clearInterval(id);
 }, []);

 // Close on outside click
 useEffect(() => {
 const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
 document.addEventListener('mousedown', handler);
 return () => document.removeEventListener('mousedown', handler);
 }, []);

 const handleLogout = () => {
 setOpen(false);
 onLogout && onLogout();
 };

 const username = user?.username || 'admin';
 const role = user?.role || 'admin';
 const initial = username[0].toUpperCase();

 // Role accent color
 const roleColor = role === 'admin' ? '#4dd9ac' : '#00b4d8';

 return (
 <div ref={ref} style={{ position: 'relative' }}>

 {/* ── Trigger chip ── */}
 <button
 onClick={() => setOpen(o => !o)}
 style={{
 display: 'flex',
 alignItems: 'center',
 gap: 8,
 background: open
 ? (dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')
 : (dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
 border: `1px solid ${open ? accent + '66' : t.border2}`,
 borderRadius: 4,
 padding: '5px 10px 5px 6px',
 cursor: 'pointer',
 transition: 'all 0.15s',
 }}
 >
 {/* Avatar */}
 <div style={{
 width: 26,
 height: 26,
 borderRadius: '50%',
 background: `linear-gradient(135deg, ${accent}, ${accent}88)`,
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'center',
 fontFamily: "'Barlow Condensed', sans-serif",
 fontSize: 13,
 fontWeight: 700,
 color: dark ? '#0a1628' : '#fff',
 flexShrink: 0,
 boxShadow: `0 0 0 2px ${accent}33`,
 }}>
 {initial}
 </div>

 {/* Name + role */}
 <div style={{ textAlign: 'left', lineHeight: 1.25 }}>
 <div style={{
 fontFamily: "'Barlow', sans-serif",
 fontSize: 13,
 fontWeight: 600,
 color: t.text,
 letterSpacing: '0.01em',
 whiteSpace: 'nowrap',
 }}>
 {username}
 </div>
 <div style={{
 fontFamily: "'IBM Plex Mono', monospace",
 fontSize: 9,
 textTransform: 'uppercase',
 letterSpacing: '0.12em',
 color: roleColor,
 }}>
 {role}
 </div>
 </div>

 {/* Chevron */}
 <span style={{
 color: t.muted,
 display: 'flex',
 alignItems: 'center',
 transform: open ? 'rotate(180deg)' : 'none',
 transition: 'transform 0.18s ease',
 marginLeft: 2,
 }}>
 <Ic d={ICONS.chevronD} size={11} />
 </span>
 </button>

 {/* ── Dropdown panel ── */}
 {open && (
 <div style={{
 position: 'absolute',
 top: 'calc(100% + 8px)',
 right: 0,
 width: 220,
 background: t.topbar,
 border: `1px solid ${t.border2}`,
 borderRadius: 4,
 boxShadow: dark
 ? '0 8px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)'
 : '0 8px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
 zIndex: 999,
 overflow: 'hidden',
 animation: 'dropIn 0.16s ease',
 }}>

 {/* Header */}
 <div style={{
 padding: '14px 14px 12px',
 borderBottom: `1px solid ${t.border}`,
 background: dark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
 {/* Large avatar */}
 <div style={{
 width: 38,
 height: 38,
 borderRadius: '50%',
 background: `linear-gradient(135deg, ${accent}, ${accent}77)`,
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'center',
 fontFamily: "'Barlow Condensed', sans-serif",
 fontSize: 18,
 fontWeight: 700,
 color: dark ? '#0a1628' : '#fff',
 flexShrink: 0,
 boxShadow: `0 0 0 3px ${accent}2a`,
 }}>
 {initial}
 </div>
 <div>
 <div style={{
 fontFamily: "'Barlow', sans-serif",
 fontSize: 14,
 fontWeight: 600,
 color: t.text,
 letterSpacing: '0.01em',
 }}>
 {username}
 </div>
 <div style={{
 fontFamily: "'IBM Plex Mono', monospace",
 fontSize: 10,
 color: roleColor,
 textTransform: 'uppercase',
 letterSpacing: '0.1em',
 marginTop: 2,
 display: 'flex',
 alignItems: 'center',
 gap: 5,
 }}>
 <span style={{
 width: 6,
 height: 6,
 borderRadius: '50%',
 background: roleColor,
 display: 'inline-block',
 }} />
 {role}
 </div>
 </div>
 </div>
 </div>

 {/* Session info rows */}
 <div style={{ padding: '10px 14px', borderBottom: `1px solid ${t.border}` }}>
 {[
 { icon: ICONS.clock, label: 'Session', val: sessionTime },
 { icon: ICONS.shield, label: 'Auth', val: 'JWT · Active' },
 ].map(row => (
 <div key={row.label} style={{
 display: 'flex',
 alignItems: 'center',
 gap: 8,
 padding: '5px 0',
 fontFamily: "'IBM Plex Mono', monospace",
 fontSize: 11,
 }}>
 <span style={{ color: t.muted, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
 <Ic d={row.icon} size={12} />
 </span>
 <span style={{ color: t.textSub, flex: 1 }}>{row.label}</span>
 <span style={{ color: t.text, fontWeight: 500 }}>{row.val}</span>
 </div>
 ))}
 </div>

 {/* Admin Panel button — only for admins */}
 {user?.role === 'admin' && onOpenAdmin && (
 <div style={{ padding: '8px 8px 0' }}>
 <button
 onClick={() => { setOpen(false); onOpenAdmin(); }}
 style={{
 width: '100%',
 display: 'flex',
 alignItems: 'center',
 gap: 8,
 padding: '8px 10px',
 background: dark ? 'rgba(0,180,216,0.08)' : 'rgba(0,48,135,0.06)',
 border: `1px solid ${dark ? 'rgba(0,180,216,0.25)' : 'rgba(0,48,135,0.2)'}`,
 borderRadius: 3,
 color: dark ? '#00b4d8' : '#003087',
 cursor: 'pointer',
 fontFamily: "'IBM Plex Mono', monospace",
 fontSize: 11,
 letterSpacing: '0.08em',
 textTransform: 'uppercase',
 transition: 'all 0.15s',
 }}
 onMouseEnter={e => {
 e.currentTarget.style.background = dark ? 'rgba(0,180,216,0.16)' : 'rgba(0,48,135,0.12)';
 e.currentTarget.style.borderColor = dark ? 'rgba(0,180,216,0.5)' : 'rgba(0,48,135,0.4)';
 }}
 onMouseLeave={e => {
 e.currentTarget.style.background = dark ? 'rgba(0,180,216,0.08)' : 'rgba(0,48,135,0.06)';
 e.currentTarget.style.borderColor = dark ? 'rgba(0,180,216,0.25)' : 'rgba(0,48,135,0.2)';
 }}
 >
 <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
 stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
 <path d="M8 1l6 3v4c0 3.5-2.5 6-6 7-3.5-1-6-3.5-6-7V4l6-3z"/>
 </svg>
 Admin Panel
 </button>
 </div>
 )}

 {/* Sign out button */}
 <div style={{ padding: '8px' }}>
 <button
 onClick={handleLogout}
 style={{
 width: '100%',
 display: 'flex',
 alignItems: 'center',
 gap: 8,
 padding: '8px 10px',
 background: 'transparent',
 border: `1px solid ${dark ? 'rgba(240,96,96,0.25)' : 'rgba(240,96,96,0.2)'}`,
 borderRadius: 3,
 color: '#f06060',
 cursor: 'pointer',
 fontFamily: "'IBM Plex Mono', monospace",
 fontSize: 11,
 letterSpacing: '0.08em',
 textTransform: 'uppercase',
 transition: 'all 0.15s',
 }}
 onMouseEnter={e => {
 e.currentTarget.style.background = 'rgba(240,96,96,0.1)';
 e.currentTarget.style.borderColor = 'rgba(240,96,96,0.4)';
 }}
 onMouseLeave={e => {
 e.currentTarget.style.background = 'transparent';
 e.currentTarget.style.borderColor = dark ? 'rgba(240,96,96,0.25)' : 'rgba(240,96,96,0.2)';
 }}
 >
 <Ic d={ICONS.logout} size={13} />
 Sign Out
 </button>
 </div>
 </div>
 )}

 {/* Dropdown animation */}
 <style>{`
 @keyframes dropIn {
 from { opacity: 0; transform: translateY(-6px) scale(0.97); }
 to { opacity: 1; transform: translateY(0) scale(1); }
 }
 `}</style>
 </div>
 );
}

// ─── Main layout ──────────────────────────────────────────────────────────────
export default function DashboardLayout({
 activeType,
 setActiveType,
 activeTab,
 setActiveTab,
 reportReady,
 dark,
 setDark,
 onLogout,
 user, // { username, role } – pass from App or wherever auth lives
 onOpenAdmin, // called when admin clicks "Admin Panel" in user dropdown
 children,
}) {
 const [collapsed, setCollapsed] = useState(false);

 const currentRt = REPORT_TYPES.find(r => r.id === activeType) || REPORT_TYPES[0];
 const accent = currentRt.accent;

 // ── Theme ────────────────────────────────────────────────────────────────
 const t = dark ? {
 bg: '#0a1628',
 sidebar: '#0b1c34',
 topbar: '#0f2040',
 border: 'rgba(255,255,255,0.07)',
 border2: 'rgba(255,255,255,0.13)',
 text: '#e8edf4',
 textSub: '#8aaac8',
 muted: '#3d5a78',
 navy: '#003087',
 cardBg: '#0f2040',
 cardBorder: 'rgba(255,255,255,0.08)',
 inputBg: '#162a50',
 inputBorder: 'rgba(255,255,255,0.13)',
 sectionBg: '#162a50',
 labelColor: '#8aaac8',
 placeholder: '#3d5a78',
 hoverBg: 'rgba(255,255,255,0.04)',
 focusRing: 'rgba(255,255,255,0.15)',
 } : {
 bg: '#f4f7fb',
 sidebar: '#ffffff',
 topbar: '#ffffff',
 border: 'rgba(0,0,0,0.08)',
 border2: 'rgba(0,0,0,0.13)',
 text: '#1a2a3a',
 textSub: '#5a7a9a',
 muted: '#a0b4c8',
 navy: '#003087',
 cardBg: '#ffffff',
 cardBorder: 'rgba(0,0,0,0.09)',
 inputBg: '#ffffff',
 inputBorder: 'rgba(0,0,0,0.15)',
 sectionBg: '#f0f4f9',
 labelColor: '#4a6880',
 placeholder: '#a0b4c8',
 hoverBg: 'rgba(0,0,0,0.03)',
 focusRing: 'rgba(0,0,0,0.1)',
 };

 const W = collapsed ? 70 : 290;

 return (
 <div style={{ display:'flex', height:'100vh', background:t.bg, fontFamily:"'Barlow',sans-serif", overflow:'hidden' }}>

 {/* ── SIDEBAR ──────────────────────────────────────────────────────── */}
 <div style={{
 width: W, flexShrink: 0, transition: 'width 0.22s ease',
 background: t.sidebar, borderRight: `1px solid ${t.border2}`,
 display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 10,
 boxShadow: dark ? '2px 0 16px rgba(0,0,0,0.35)' : '2px 0 10px rgba(0,0,0,0.07)',
 }}>

 {/* Logo */}
 <div style={{
 padding: collapsed ? '16px 0' : '16px 20px',
 borderBottom: `1px solid ${t.border}`,
 display: 'flex', alignItems: 'center',
 justifyContent: collapsed ? 'center' : 'space-between',
 }}>
 {!collapsed ? (
 <>
 <OneOmicsLogo height={40} maxWidth="190px" dark={dark} />
 <button onClick={() => setCollapsed(true)} style={{
 background:'none', border:`1px solid ${t.border}`, borderRadius: 4,
 color: t.muted, cursor: 'pointer', padding: '5px 7px',
 display: 'flex', alignItems: 'center',
 }}>
 <Ic d={ICONS.chevronL} size={14} />
 </button>
 </>
 ) : (
 <OneOmicsLogo height={32} dark={dark} collapsed={true} onClick={() => setCollapsed(false)} />
 )}
 </div>

 {/* Dashboard home nav item */}
 {!collapsed && (
 <div
 onClick={() => setActiveType('dashboard')}
 style={{
 display:'flex', alignItems:'center', gap:14,
 padding:'13px 20px',
 borderLeft:`3px solid ${activeType === 'dashboard' ? '#00b4d8' : 'transparent'}`,
 background: activeType === 'dashboard'
 ? (dark ? 'rgba(0,180,216,0.09)' : 'rgba(0,180,216,0.07)')
 : 'transparent',
 cursor:'pointer', transition:'all 0.14s',
 borderBottom:`1px solid ${t.border}`,
 }}
 >
 <span style={{ color: activeType === 'dashboard' ? '#00b4d8' : t.muted, display:'flex', alignItems:'center' }}>
 <Ic d="M1 1h6v6H1zm8 0h6v6H9zM1 9h6v6H1zm8 0h6v6H9z" size={20} />
 </span>
 <span style={{ fontFamily:"'Barlow',sans-serif", fontSize:16, fontWeight: activeType === 'dashboard' ? 600 : 400, color: activeType === 'dashboard' ? '#00b4d8' : t.text }}>
 Dashboard
 </span>
 </div>
 )}
 {collapsed && (
 <div
 title="Dashboard"
 onClick={() => setActiveType('dashboard')}
 style={{
 display:'flex', alignItems:'center', justifyContent:'center',
 padding:'14px 0',
 borderLeft:`3px solid ${activeType === 'dashboard' ? '#00b4d8' : 'transparent'}`,
 background: activeType === 'dashboard' ? 'rgba(0,180,216,0.09)' : 'transparent',
 cursor:'pointer', transition:'all 0.14s',
 }}
 >
 <span style={{ color: activeType === 'dashboard' ? '#00b4d8' : t.muted }}>
 <Ic d="M1 1h6v6H1zm8 0h6v6H9zM1 9h6v6H1zm8 0h6v6H9z" size={20} />
 </span>
 </div>
 )}

 {/* Section label */}
 {!collapsed && (
 <div style={{
 fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, textTransform: 'uppercase',
 letterSpacing: '0.16em', color: t.muted, padding: '16px 20px 8px',
 }}>Report Types</div>
 )}

 {/* Nav items */}
 <div style={{ flex:1, overflowY:'auto', overflowX:'hidden', paddingTop: collapsed ? 8 : 2 }}>
 {REPORT_TYPES.map(rt => {
 const isActive = activeType === rt.id;
 const hasReport = reportReady?.[rt.id];

 return (
 <div key={rt.id}>
 <div
 title={collapsed ? rt.label : undefined}
 onClick={() => { setActiveType(rt.id); setActiveTab('upload'); }}
 style={{
 display: 'flex', alignItems: 'center',
 gap: collapsed ? 0 : 14,
 padding: collapsed ? '14px 0' : '13px 20px',
 justifyContent: collapsed ? 'center' : 'flex-start',
 borderLeft: `3px solid ${isActive ? rt.accent : 'transparent'}`,
 background: isActive
 ? (dark ? `${rt.accent}14` : `${rt.accent}12`)
 : 'transparent',
 cursor: 'pointer', transition: 'all 0.14s', position: 'relative',
 }}
 >
 <span style={{ color: isActive ? rt.accent : t.muted, display: 'flex', alignItems: 'center', flexShrink: 0, transition: 'color 0.14s' }}>
 <Ic d={ICONS[rt.icon]} size={20} />
 </span>
 {!collapsed && (
 <div style={{ minWidth: 0, flex: 1 }}>
 <div style={{ fontFamily:"'Barlow',sans-serif", fontSize:16, fontWeight: isActive ? 600 : 400, color: isActive ? rt.accent : t.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', lineHeight:1.3 }}>{rt.label}</div>
 <div style={{ fontFamily:"'Barlow',sans-serif", fontSize:12, color:t.muted, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginTop:2 }}>{rt.sub}</div>
 </div>
 )}
 {!collapsed && hasReport && (
 <span style={{ width:6, height:6, borderRadius:'50%', background:rt.accent, flexShrink:0 }} />
 )}
 </div>

 {isActive && !collapsed && (
 <div style={{ background: dark ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.025)', paddingBottom:4 }}>
 <div
 onClick={() => setActiveTab('upload')}
 style={{
 display:'flex', alignItems:'center', gap:9,
 padding:'10px 20px 10px 52px', cursor:'pointer',
 borderLeft:`3px solid ${activeTab === 'upload' ? rt.accent : 'transparent'}`,
 background: activeTab === 'upload' ? (dark ? `${rt.accent}18` : `${rt.accent}10`) : 'transparent',
 transition:'all 0.12s',
 }}
 >
 <span style={{ color: activeTab === 'upload' ? rt.accent : t.muted, display:'flex', alignItems:'center' }}><Ic d={ICONS.upload} size={15} /></span>
 <span style={{ fontFamily:"'Barlow',sans-serif", fontSize:14, color: activeTab === 'upload' ? rt.accent : t.textSub, fontWeight: activeTab === 'upload' ? 500 : 400 }}>Upload Files</span>
 </div>
 </div>
 )}

 <div style={{ height:'1px', background:t.border, margin:'0 10px' }} />
 </div>
 );
 })}
 </div>

 {/* Footer */}
 <div style={{ borderTop:`1px solid ${t.border}` }}>
 <div style={{
 padding: collapsed ? '12px 0' : '12px 18px',
 display:'flex', alignItems:'center',
 justifyContent: collapsed ? 'center' : 'space-between', gap:8,
 }}>
 {!collapsed && (
 <div style={{ minWidth:0, flex:1 }}>
 <div style={{ fontFamily:"'Barlow',sans-serif", fontSize:13, color:t.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
 admin@oneomics.io
 </div>
 <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:t.muted, textTransform:'uppercase', letterSpacing:'0.08em', marginTop:2 }}>
 Admin
 </div>
 </div>
 )}
 <div style={{ display:'flex', gap:5, flexShrink:0 }}>
 <button onClick={() => setDark(d => !d)} title={dark ? 'Light mode' : 'Dark mode'} style={{
 background:'none', border:`1px solid ${t.border}`, borderRadius:3,
 color:t.muted, cursor:'pointer', padding:'5px 6px',
 display:'flex', alignItems:'center',
 }}>
 <Ic d={dark ? ICONS.sun : ICONS.moon} size={13} />
 </button>
 {onLogout && (
 <button onClick={onLogout} title="Log out" style={{
 background:'none', border:'none', color:t.muted,
 cursor:'pointer', padding:'5px 6px', display:'flex', alignItems:'center',
 }}>
 <Ic d={ICONS.logout} size={13} />
 </button>
 )}
 </div>
 </div>
 </div>
 </div>

 {/* ── MAIN ─────────────────────────────────────────────────────────── */}
 <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>

 {/* Top bar */}
 <div style={{
 height:52, flexShrink:0, background:t.topbar,
 borderBottom:`1px solid ${t.border}`,
 display:'flex', alignItems:'center', padding:'0 24px', gap:14,
 }}>
 {/* Accent dot */}
 <div style={{ width:8, height:8, borderRadius:'50%', background:accent, flexShrink:0 }} />

 {/* Breadcrumb */}
 <div style={{ flex:1, display:'flex', alignItems:'center', gap:8 }}>
 <span style={{
 fontFamily:"'Barlow Condensed',sans-serif", fontSize:17, fontWeight:600,
 color:t.text, letterSpacing:'0.02em',
 }}>
 {activeType === 'dashboard' ? 'Dashboard' : currentRt.label}
 </span>
 {activeType !== 'dashboard' && (
 <>
 <span style={{ color:t.muted, fontSize:14 }}>›</span>
 <span style={{
 fontFamily:"'Barlow Condensed',sans-serif", fontSize:15, fontWeight:500,
 color:accent, letterSpacing:'0.02em',
 }}>
 {activeTab === 'upload' ? 'Upload Files' : ''}
 </span>
 </>
 )}
 </div>

 {/* ── USER CHIP — replaces Overview badge ── */}
 <UserChip t={t} dark={dark} accent={accent} user={user} onLogout={onLogout} onOpenAdmin={onOpenAdmin} />

 {/* Theme toggle */}
 <button
 onClick={() => setDark(d => !d)}
 title={dark ? 'Light mode' : 'Dark mode'}
 style={{
 background:'none', border:`1px solid ${t.border}`, borderRadius:3,
 color:t.muted, cursor:'pointer', padding:'5px 8px',
 display:'flex', alignItems:'center',
 }}
 >
 <Ic d={dark ? ICONS.sun : ICONS.moon} size={14} />
 </button>
 </div>

 {/* Scrollable content */}
 <div style={{ flex:1, overflowY:'auto', background:t.bg }}>
 <style>{`
 @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=Barlow+Condensed:wght@400;500;600;700&family=Barlow:wght@400;500;600&display=swap');

 .min-h-screen { background: ${t.bg} !important; }
 .bg-white {
 background: ${t.cardBg} !important;
 border-color: ${t.cardBorder} !important;
 box-shadow: ${dark ? '0 2px 16px rgba(0,0,0,0.3)' : '0 1px 8px rgba(0,0,0,0.06)'} !important;
 }
 .bg-gray-50 { background: ${t.sectionBg} !important; }
 .text-gray-800, .text-gray-900 { color: ${t.text} !important; }
 .text-gray-700 { color: ${t.textSub} !important; }
 .text-gray-600, .text-gray-500 { color: ${dark ? '#5a7a9a' : '#6a8aaa'} !important; }
 .text-gray-400 { color: ${t.muted} !important; }
 input:not([type='file']):not([type='checkbox']):not([type='radio']),
 textarea, select {
 background: ${t.inputBg} !important;
 border-color: ${t.inputBorder} !important;
 color: ${t.text} !important;
 font-size: 14px !important;
 }
 input::placeholder, textarea::placeholder { color: ${t.placeholder} !important; }
 input:focus, textarea:focus, select:focus {
 border-color: ${accent} !important;
 box-shadow: 0 0 0 3px ${t.focusRing} !important;
 outline: none !important;
 }
 .border-gray-200, .border-b, .border-t { border-color: ${t.cardBorder} !important; }
 .border-dashed { border-color: ${t.inputBorder} !important; }
 .border-dashed:hover { background: ${t.hoverBg} !important; }
 .border-gray-300 { border-color: ${t.inputBorder} !important; }
 .hover\\:border-blue-400:hover { border-color: ${accent} !important; }
 .border-green-500 { border-color: #22c55e !important; }
 .bg-green-50 { background: ${dark ? 'rgba(34,197,94,0.07)' : '#f0fdf4'} !important; }
 .text-green-700 { color: ${dark ? '#4ade80' : '#15803d'} !important; }
 .bg-green-500 { background: #22c55e !important; }
 .bg-blue-600 { background: ${dark ? '#0d47a1' : '#003087'} !important; }
 .hover\\:bg-blue-700:hover { background: ${dark ? '#1565c0' : '#002070'} !important; }
 .bg-gray-400 { background: ${dark ? '#2a3a4a' : '#c0ccd8'} !important; }
 strong { color: ${t.text} !important; }
 ${dark ? `
 ::-webkit-scrollbar { width: 5px; }
 ::-webkit-scrollbar-track { background: #0a1628; }
 ::-webkit-scrollbar-thumb { background: #1e3460; border-radius: 3px; }
 ` : ''}
 `}</style>
 {children}
 </div>
 </div>
 </div>
 );
}